/** Catégories du garage unifié (tabs). */
export type GarageCategory = 'circuits' | 'voitures' | 'pilotes' | 'modeles-3d';

export type GarageRoute = {
  category: GarageCategory;
  /** Id numérique (circuits/voitures/pilotes) ou slug (modèles 3D). */
  id: string | null;
};

export const GARAGE_CATEGORIES: readonly GarageCategory[] = [
  'circuits',
  'voitures',
  'pilotes',
  'modeles-3d',
] as const;

export const GARAGE_CATEGORY_LABELS: Record<GarageCategory, string> = {
  circuits: 'Circuits',
  voitures: 'Voitures',
  pilotes: 'Pilotes',
  'modeles-3d': 'Modèles 3D',
};

const CATEGORY_SET = new Set<string>(GARAGE_CATEGORIES);

/** Défaut quand `#garage` sans sous-chemin. */
export const DEFAULT_GARAGE_CATEGORY: GarageCategory = 'circuits';

/**
 * Parse un hash (avec ou sans `#`) en route garage.
 * Retourne `null` si ce n'est pas une route garage.
 */
export function parseGarageHash(hash: string): GarageRoute | null {
  const raw = hash.replace(/^#/, '').trim();
  const path = (raw.split('?')[0] ?? '').replace(/^\/+/, '');
  const parts = path.split('/').filter(Boolean);
  if (parts[0] !== 'garage') return null;

  const catRaw = parts[1];
  const category: GarageCategory =
    catRaw && CATEGORY_SET.has(catRaw) ? (catRaw as GarageCategory) : DEFAULT_GARAGE_CATEGORY;
  const id = parts[2]?.trim() || null;
  return { category, id };
}

/** Construit le hash sans `#` : `garage/voitures/12`. */
export function buildGarageHash(route: GarageRoute): string {
  const base = `garage/${route.category}`;
  if (route.id) return `${base}/${encodeURIComponent(route.id)}`;
  return base;
}

const LEGACY_MAP: Record<string, GarageCategory> = {
  circuits: 'circuits',
  voitures: 'voitures',
  pilotes: 'pilotes',
  assets: 'modeles-3d',
};

/**
 * Si le hash est une ancienne URL autonome, retourne le hash garage cible
 * (sans `#`). Sinon `null`.
 */
export function redirectLegacyGarageHash(hash: string): string | null {
  const raw = hash.replace(/^#/, '').trim();
  const path = (raw.split('?')[0] ?? '').replace(/^\/+/, '');
  const parts = path.split('/').filter(Boolean);
  const head = parts[0];
  if (!head) return null;
  const category = LEGACY_MAP[head];
  if (!category) return null;
  const id = parts[1]?.trim() || null;
  return buildGarageHash({ category, id });
}

/** Id numérique pour circuits / voitures / pilotes ; `null` si invalide. */
export function parseGarageNumericId(id: string | null): number | null {
  if (!id) return null;
  const n = parseInt(id, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}
